import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { browserApiUrl, customInstance } from '../services/api-client';
import {
  applyOntologyGenerationSuggestionsBatchApiV1WorkspacesWorkspaceIdSemanticContextOntologyGenerationRunsRunIdSuggestionsApplyPost,
  getSemanticContextSetupReadinessApiV1WorkspacesWorkspaceIdSemanticContextSetupReadinessGet,
  runSemanticContextSetupReadinessSmokeApiV1WorkspacesWorkspaceIdSemanticContextSetupReadinessSmokePost,
} from '../generated/workspace-semantic-context/workspace-semantic-context';
import type {
  StructuredAnalyticsDraft,
  StructuredAssetAllowlistRow,
  WorkspaceCatalogCoverageRow,
  WorkspaceOntology,
  TextToSqlReadinessState,
  StructuredReadinessBlocker,
  StructuredReadinessActionTarget,
} from '../components/workspace-text-to-sql/structuredAnalyticsConfig';
import type {
  OntologyGenerationConnectionState,
  OntologyGenerationRunEvent,
} from '../components/workspace-text-to-sql/ontologyBuildProjection';

export type {
  OntologyGenerationConnectionState,
  OntologyGenerationRunEvent,
} from '../components/workspace-text-to-sql/ontologyBuildProjection';

export interface SemanticContextConnectorSummary {
  id: number;
  connector_id: string;
  connector_name: string;
  connector_type: string;
  is_enabled: boolean;
  is_healthy: boolean;
  health_check_message?: string | null;
  updated_at?: string | null;
}

export interface SemanticContextTableSummary {
  id: number;
  project?: string | null;
  dataset?: string | null;
  name?: string | null;
  table_external_id: string;
  source_description?: string | null;
  row_count_snapshot?: number | null;
  last_synced_at?: string | null;
  is_active: boolean;
  has_drift: boolean;
  selected: boolean;
  catalog_link_count: number;
}

export interface SemanticContextPackDeltaItem {
  section: string;
  change_type: 'added' | 'removed' | 'modified';
  key: string;
  label: string;
  fields: string[];
  before?: string | null;
  after?: string | null;
}

export interface SemanticContextPackDeltaSummary {
  base_pack_id?: number | null;
  base_version?: number | null;
  target_pack_id?: number | null;
  target_version?: number | null;
  total_changes: number;
  entity_changes: number;
  metric_changes: number;
  relationship_changes: number;
  verified_query_changes: number;
  table_changes: number;
  changes: SemanticContextPackDeltaItem[];
}

export interface SemanticContextPackSummary {
  id: number;
  version: number;
  status: string;
  is_active: boolean;
  validation_summary?: Record<string, unknown> | null;
  configuration_ref?: Record<string, unknown> | null;
  ontology_ref?: Record<string, unknown> | null;
  compiled_at?: string | null;
  compiled_by_user_id?: number | null;
  activated_at?: string | null;
  activated_by_user_id?: number | null;
  activation_metadata?: Record<string, unknown> | null;
  failure_reason?: string | null;
  delta_from_previous?: SemanticContextPackDeltaSummary | null;
  delta_from_active?: SemanticContextPackDeltaSummary | null;
}

export interface SemanticContextReadinessResponse {
  workspace_id: number;
  status: 'needs_setup' | 'configured' | 'draft_pending' | 'ready' | 'blocked';
  blockers: Array<{ code: string; severity: 'error' | 'warning'; message: string }>;
  warehouse_connector?: SemanticContextConnectorSummary | null;
  selected_table_count: number;
  ontology_entity_count: number;
  ontology_metric_count: number;
  ontology_relationship_count: number;
  active_pack?: SemanticContextPackSummary | null;
  latest_pack?: SemanticContextPackSummary | null;
  draft_delta?: SemanticContextPackDeltaSummary | null;
  catalog_link_count: number;
}

export interface SemanticContextRuntimePackListResponse {
  workspace_id: number;
  packs: SemanticContextPackSummary[];
}

export type SemanticContextSetupStatus = 'ready' | 'blocked' | 'warning' | 'in_progress' | 'unsupported';
export type SemanticContextSetupStageState = 'complete' | 'blocked' | 'warning' | 'not_started' | 'in_progress';

export interface SemanticContextSetupBlocker {
  stage_key: string;
  code: string;
  severity: 'error' | 'warning';
  message: string;
  next_action: string;
  ui_target?: string | null;
  api_target?: string | null;
  blocks_runtime: boolean;
}

export interface SemanticContextSetupStage {
  key: string;
  label: string;
  state: SemanticContextSetupStageState;
  reason: string;
  next_action?: string | null;
  ui_target?: string | null;
  api_target?: string | null;
  blocks_runtime: boolean;
  blocker_code?: string | null;
  evidence?: Record<string, string | number | boolean | null>;
}

export interface SemanticContextSetupReadinessResponse {
  workspace_id: number;
  overall_status: SemanticContextSetupStatus;
  first_blocker?: SemanticContextSetupBlocker | null;
  safe_explanation?: string;
  next_action?: string | null;
  action_ui_target?: string | null;
  action_api_target?: string | null;
  stages: SemanticContextSetupStage[];
  structured_scope_summary: {
    connector_configured: boolean;
    connector_healthy: boolean;
    selected_table_count: number;
    scoped_table_count: number;
  };
  catalog_coverage_summary: {
    described_table_count: number;
    selected_table_count: number;
    missing_description_count: number;
    required: boolean;
  };
  ontology_summary: {
    entity_count: number;
    metric_count: number;
    relationship_count: number;
    semantic_mapping_count: number;
    pending_suggestion_count: number;
    latest_generation_status?: string | null;
  };
  runtime_pack_summary: {
    active_pack?: SemanticContextPackSummary | null;
    latest_pack?: SemanticContextPackSummary | null;
    stale_warning: boolean;
    unusable: boolean;
    warning_codes: string[];
  };
  mcp_summary?: {
    status: string;
    effective_status?: string | null;
    first_blocker_code?: string | null;
    first_blocker_message?: string | null;
    first_blocker_next_action?: string | null;
  } | null;
  smoke_summary?: {
    executed: boolean;
    connector_health_status: string;
    dry_run_status: string;
    sql_execution_status: string;
    bytes_limit?: number | null;
    row_limit?: number | null;
  };
  capabilities: {
    workspace_kind?: string | null;
    chat_backend?: string | null;
    supports_structured_warehouse_scope: boolean;
    supports_ontology_authoring: boolean;
    supports_context_packs: boolean;
    supports_mcp_exposure: boolean;
  };
}

export interface WorkspaceWarehouseProfilingBudget {
  maximum_bytes_billed?: number;
  maximum_total_bytes_billed?: number;
  maximum_rows?: number;
  timeout_seconds?: number;
  sample_percent?: number;
  maximum_tables?: number;
  maximum_columns_per_table?: number;
  maximum_join_candidates?: number;
  maximum_run_seconds?: number;
}

export interface WorkspaceWarehouseProfilingRun {
  id: number;
  workspace_id: number;
  connector_id: number;
  requested_by_user_id?: number | null;
  status: string;
  source_fingerprint: string;
  scope_snapshot: Record<string, unknown>;
  budget: WorkspaceWarehouseProfilingBudget;
  progress: Record<string, unknown>;
  omissions: Array<Record<string, unknown>>;
  diagnostics?: Record<string, unknown> | null;
  error_message?: string | null;
  celery_task_id?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface WorkspaceWarehouseJoinEvidence {
  id: number;
  run_id: number;
  ontology_relationship_id?: number | null;
  source_table_id: number;
  target_table_id: number;
  candidate_fingerprint: string;
  source_column_ids: number[];
  target_column_ids: number[];
  source_column_names: string[];
  target_column_names: string[];
  validation_state: string;
  review_state: string;
  is_executable: boolean;
  match_rate?: number | null;
  source_null_rate?: number | null;
  target_null_rate?: number | null;
  source_uniqueness_rate?: number | null;
  target_uniqueness_rate?: number | null;
  cardinality?: string | null;
  fanout_ratio?: number | null;
  unsafe_fanout: boolean;
  query_bytes_processed?: number | null;
  query_cost_estimate?: number | null;
  omission_reason?: string | null;
  reviewed_by_user_id?: number | null;
  reviewer_notes?: string | null;
  reviewed_at?: string | null;
  validated_at?: string | null;
}

export interface WorkspaceWarehouseProfilingEvidence {
  tables: Array<Record<string, unknown>>;
  columns: Array<Record<string, unknown>>;
  joins: WorkspaceWarehouseJoinEvidence[];
}

export type WorkspaceWarehouseJoinReviewAction = 'approve' | 'reject' | 'steward_exception';

export interface SemanticContextStructuredScopeResponse {
  workspace_id: number;
  warehouse_connector?: SemanticContextConnectorSummary | null;
  scoped_tables: SemanticContextTableSummary[];
  selected_table_ids: number[];
}

export interface SemanticContextOntologyResponse {
  entities: Array<{
    id?: number | null;
    name: string;
    display_name: string;
    description?: string | null;
    aliases?: string[] | null;
    ontology_ref?: string | null;
    table_id?: number | null;
    source_table_ids?: number[];
    source_bindings?: Array<{
      table_id: number;
      binding_role?: string | null;
      source_authority?: string | null;
      identity_columns?: unknown[];
      identity_mappings?: Array<{
        mapping_type: 'exact' | 'normalized' | 'lookup_bridge' | 'value_overlap' | 'probabilistic';
        local_column_ids?: number[];
        local_column_names?: string[];
        counterpart_table_id?: number | null;
        counterpart_column_ids?: number[];
        counterpart_column_names?: string[];
        normalization_rules?: Array<Record<string, unknown>>;
        evidence_refs?: string[];
        confidence?: number;
      }>;
      normalization_rules?: Array<Record<string, unknown>>;
      match_method?: string | null;
      match_evidence?: Array<Record<string, unknown>>;
      grain?: Record<string, unknown> | null;
      evidence?: Record<string, unknown> | unknown[] | null;
      confidence?: number;
      review_state?: string;
      execution_mode?: string;
      trust_contract?: {
        authority_tier?: 'system_of_record' | 'authoritative' | 'supporting' | 'derived' | 'unknown';
        permitted_uses?: Array<'identity_resolution' | 'enrichment' | 'filtering' | 'aggregation' | 'display'>;
        conflict_policy?: 'primary_wins' | 'highest_confidence' | 'steward_required' | 'reject';
        requires_steward_review?: boolean;
        notes?: string | null;
      } | null;
      conflict_state?: string;
      conflicts?: unknown[];
    }>;
    catalog_asset_id?: number | null;
  }>;
  semantic_mappings?: Array<{
    id?: number | null;
    concept_key: string;
    label: string;
    description?: string | null;
    aliases?: string[] | null;
    entity_id?: number | null;
    table_id: number;
    column_id: number;
    catalog_asset_id?: number | null;
    default_aggregation?: string | null;
    value_kind?: string | null;
    semantic_metadata?: Record<string, unknown> | null;
  }>;
  metrics: Array<{
    id?: number | null;
    name: string;
    display_name: string;
    description?: string | null;
    entity_id?: number | null;
    sql_formula: string;
    expression_metadata?: Record<string, unknown> | null;
    primary_column_id?: number | null;
    unit?: string | null;
    format_type?: string | null;
  }>;
  relationships: Array<{
    id?: number | null;
    name?: string | null;
    description?: string | null;
    source_entity_id: number;
    target_entity_id: number;
    relationship_type: string;
    relationship_kind?: string | null;
    predicate?: string | null;
    inverse_predicate?: string | null;
    source_role?: string | null;
    target_role?: string | null;
    optionality?: string | null;
    join_on?: string | null;
    join_type: string;
    source_table_id?: number | null;
    target_table_id?: number | null;
    join_conditions?: Array<Record<string, unknown>> | null;
    join_evidence_status?: string | null;
    traversal_allowed?: boolean | null;
    relationship_validation?: Record<string, unknown> | null;
    cardinality?: string | null;
    cardinality_warning?: string | null;
    status?: string | null;
  }>;
}

export interface SemanticContextOverviewResponse {
  readiness: SemanticContextReadinessResponse;
  structured_scope: SemanticContextStructuredScopeResponse;
  ontology: SemanticContextOntologyResponse;
}

export type SemanticCorrectionSourceSurface = 'chat' | 'mcp' | 'catalog' | 'ontology_builder' | 'system';
export type SemanticCorrectionTargetScope = 'workspace' | 'catalog' | 'triage';
export type SemanticCorrectionKind =
  | 'ontology_alias'
  | 'ontology_metric'
  | 'ontology_entity'
  | 'ontology_relationship'
  | 'ontology_default_filter'
  | 'warehouse_column_alias'
  | 'catalog_asset_metadata'
  | 'catalog_column_metadata'
  | 'runtime_feedback'
  | 'unknown';

export interface WorkspaceSemanticCorrectionProposal {
  id: number;
  customer_id: string;
  workspace_id: number;
  submitted_by_user_id?: number | null;
  source_surface: SemanticCorrectionSourceSurface | string;
  source_conversation_id?: string | null;
  source_message_id?: string | null;
  source_run_id?: string | null;
  trace_id?: string | null;
  active_pack_id_at_submission?: number | null;
  active_pack_version_at_submission?: number | null;
  correction_kind: SemanticCorrectionKind | string;
  target_scope: SemanticCorrectionTargetScope | string;
  target_ref?: Record<string, unknown> | null;
  field_path?: string | null;
  original_value?: string | null;
  proposed_value?: string | null;
  proposed_patch?: Record<string, unknown> | null;
  user_text?: string | null;
  assistant_answer_snapshot?: string | null;
  generated_sql_snapshot?: string | null;
  runtime_error_snapshot?: string | null;
  evidence_payload?: Record<string, unknown> | null;
  classifier_confidence?: number | null;
  classifier_reason?: string | null;
  status: string;
  routed_catalog_correction_id?: number | null;
  reviewed_by_user_id?: number | null;
  reviewer_notes?: string | null;
  reviewed_at?: string | null;
  applied_at?: string | null;
  resulting_pack_id?: number | null;
  created_at: string;
  updated_at: string;
}

export interface WorkspaceSemanticCorrectionListResponse {
  workspace_id: number;
  proposals: WorkspaceSemanticCorrectionProposal[];
}

export interface WorkspaceSemanticCorrectionCreate {
  source_surface: SemanticCorrectionSourceSurface;
  correction_kind: SemanticCorrectionKind;
  target_scope: SemanticCorrectionTargetScope;
  target_ref?: Record<string, unknown> | null;
  field_path?: string | null;
  original_value?: string | null;
  proposed_value?: string | null;
  proposed_patch?: Record<string, unknown> | null;
  user_text?: string | null;
  assistant_answer_snapshot?: string | null;
  generated_sql_snapshot?: string | null;
  runtime_error_snapshot?: string | null;
  evidence_payload?: Record<string, unknown> | null;
  source_conversation_id?: string | null;
  source_message_id?: string | null;
  source_run_id?: string | null;
  trace_id?: string | null;
  classifier_confidence?: number | null;
  classifier_reason?: string | null;
}

export interface WorkspaceSemanticCorrectionRoute {
  target_scope: SemanticCorrectionTargetScope;
  correction_kind?: SemanticCorrectionKind | null;
  target_ref?: Record<string, unknown> | null;
  field_path?: string | null;
  reviewer_notes?: string | null;
}

export interface SemanticContextStructuredScopeUpdate {
  warehouse_connector_id?: number | null;
  table_ids: number[];
}

export interface SemanticContextCompileResponse {
  pack: SemanticContextPackSummary;
  readiness: SemanticContextReadinessResponse;
}

export interface SemanticContextMCPToolExposure {
  name: string;
  title: string;
  enabled: boolean;
  code?: string | null;
  required_permission: string;
  requires_active_context_pack: boolean;
  active_context_pack_available: boolean;
  required_workspace_capability?: string | null;
  workspace_capability_supported?: boolean;
  disabled_reason?: string | null;
}

export interface SemanticContextMCPOAuthMetadata {
  server_url: string;
  authorization_url: string;
  token_url: string;
  oauth_authorization_server_metadata_url: string;
  oauth_protected_resource_metadata_url: string;
  oauth_token_endpoint_auth_method: string;
  oauth_client_id?: string | null;
  oauth_redirect_uris: string[];
}

export interface SemanticContextMCPPolicyResponse {
  workspace_id: number;
  tenant_enabled: boolean;
  workspace_allowed: boolean;
  workspace_enabled: boolean;
  effective_enabled: boolean;
  policy_state: string;
  policy_reason: string;
  denial_reason?: string | null;
  allowed_workspace_ids: number[];
  allowed_tool_names: string[];
  active_context_pack_required: boolean;
  active_context_pack_available: boolean;
  active_context_pack_version?: number | null;
  oauth: SemanticContextMCPOAuthMetadata;
  tools: SemanticContextMCPToolExposure[];
}

export type SemanticContextMCPReadinessStatus = 'ready' | 'blocked' | 'unknown';
export type SemanticContextMCPReadinessEffectiveStatus =
  | 'ready_for_external_mcp'
  | 'blocked_by_platform_route'
  | 'blocked_by_tenant_policy'
  | 'blocked_by_workspace_policy'
  | 'blocked_by_workspace_opt_in'
  | 'blocked_by_user_permission'
  | 'blocked_by_tool_policy'
  | 'blocked_by_workspace_capability'
  | 'blocked_by_active_pack'
  | 'blocked_by_oauth_redirect_uri';
export type SemanticContextMCPReadinessCheckStatus = 'passed' | 'blocked' | 'not_executed';
export type SemanticContextMCPPolicyScope = 'all_current_and_future' | 'explicit' | 'disabled' | 'not_configured';

export interface SemanticContextMCPReadinessBlocker {
  code: string;
  message: string;
  next_action: string;
  ui_target?: string | null;
  api_target?: string | null;
}

export interface SemanticContextMCPReadinessCheck {
  key: string;
  label: string;
  status: SemanticContextMCPReadinessCheckStatus;
  code?: string | null;
  message: string;
  next_action?: string | null;
  ui_target?: string | null;
  api_target?: string | null;
}

export interface SemanticContextMCPReadinessOAuthMetadata extends SemanticContextMCPOAuthMetadata {
  redirect_uri_count: number;
}

export interface SemanticContextMCPActiveContextPackSummary {
  id?: number | null;
  version?: number | null;
  status?: string | null;
  is_active: boolean;
  compiled_at?: string | null;
  activated_at?: string | null;
}

export interface SemanticContextMCPSmokeMarker {
  status: SemanticContextMCPReadinessCheckStatus;
  code: string;
  message: string;
}

export interface SemanticContextMCPReadinessSmoke {
  list_workspaces_visibility: SemanticContextMCPSmokeMarker;
  structured_query_exposed: SemanticContextMCPSmokeMarker;
  external_chatgpt_auth: SemanticContextMCPSmokeMarker;
  real_sql_execution: SemanticContextMCPSmokeMarker;
}

export interface SemanticContextMCPReadinessResponse {
  workspace_id: number;
  status: SemanticContextMCPReadinessStatus;
  effective_status: SemanticContextMCPReadinessEffectiveStatus;
  first_blocker?: SemanticContextMCPReadinessBlocker | null;
  checks: SemanticContextMCPReadinessCheck[];
  oauth: SemanticContextMCPReadinessOAuthMetadata;
  tools: SemanticContextMCPToolExposure[];
  active_context_pack?: SemanticContextMCPActiveContextPackSummary | null;
  smoke: SemanticContextMCPReadinessSmoke;
  tenant_enabled: boolean;
  workspace_allowed: boolean;
  workspace_enabled: boolean;
  allowed_workspace_scope: SemanticContextMCPPolicyScope;
  allowed_tool_scope: SemanticContextMCPPolicyScope;
}

export interface TenantAIModelOption {
  provider_config_id: number;
  provider_type: string;
  provider_label: string;
  model_id: string;
  is_default: boolean;
  is_enabled: boolean;
  is_shared_from_platform: boolean;
}

export interface OntologyGenerationSettingsPolicy {
  enabled: boolean;
  provider_config_id?: number | null;
  model_id?: string | null;
  temperature: number;
  max_tokens: number;
  confidence_threshold: number;
  approval_mode: 'review_required' | 'disabled';
  budget?: Record<string, unknown> | null;
}

export interface OntologyGenerationSettingsResponse {
  workspace_id: number;
  applet_key: string;
  capability_key: string;
  policy: OntologyGenerationSettingsPolicy;
  model_options: TenantAIModelOption[];
  disabled_reason?: string | null;
}

export interface OntologyGenerationRunResponse {
  id: number;
  workspace_id: number;
  status: string;
  provider_config_id?: number | null;
  model_id?: string | null;
  prompt_key: string;
  prompt_version?: string | null;
  source_summary?: Record<string, unknown> | null;
  suggestion_counts?: Record<string, unknown> | null;
  error_message?: string | null;
  created_at: string;
  completed_at?: string | null;
}

export interface OntologyGenerationStreamState {
  events: OntologyGenerationRunEvent[];
  latestEvent?: OntologyGenerationRunEvent;
  completion?: OntologyGenerationRunEvent;
  failure?: OntologyGenerationRunEvent;
  runId?: number;
  isConnected: boolean;
  isStreaming: boolean;
  connectionState: OntologyGenerationConnectionState;
  isPolling: boolean;
  wasRestored: boolean;
  isPersistedReplay?: boolean;
  progressMessage?: string;
  progressPercentage?: number;
  errorMessage?: string;
}

export interface OntologySuggestionResponse {
  id: number;
  workspace_id: number;
  run_id: number;
  target_type: string;
  target_ref?: Record<string, unknown> | null;
  operation: string;
  proposed_payload: Record<string, unknown>;
  confidence_score: number;
  rationale?: string | null;
  evidence_refs?: Record<string, unknown> | null;
  model_metadata?: Record<string, unknown> | null;
  status: 'pending' | 'applied' | 'rejected' | 'superseded' | 'failed' | string;
  reviewer_id?: number | null;
  reviewer_notes?: string | null;
  created_at: string;
  reviewed_at?: string | null;
  applied_at?: string | null;
}

export type WorkspaceVerifiedQueryType = 'structured' | 'unstructured' | 'blended';

export interface WorkspaceVerifiedQuery {
  id: number;
  customer_id: string;
  workspace_id: number;
  created_by_user_id?: number | null;
  reviewer_id?: number | null;
  query_type: WorkspaceVerifiedQueryType | string;
  business_question: string;
  paraphrases: string[];
  intent_tags: string[];
  status: 'draft_ai' | 'pending_review' | 'approved' | 'rejected' | 'compiled' | 'superseded' | string;
  source_surface: string;
  sql_dialect?: string | null;
  sql_text?: string | null;
  pseudocode?: string | null;
  expected_answer_contract?: Record<string, unknown> | null;
  structured_contract?: Record<string, unknown> | null;
  unstructured_contract?: Record<string, unknown> | null;
  table_refs: string[];
  column_refs: string[];
  metric_refs: string[];
  entity_refs: string[];
  relationship_refs: string[];
  source_refs: string[];
  citation_expectations?: Record<string, unknown> | null;
  validation_method?: string | null;
  reviewer_notes?: string | null;
  reviewed_at?: string | null;
  last_verified_at?: string | null;
  compiled_pack_id?: number | null;
  compiled_pack_version?: number | null;
  ai_generation_metadata?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface WorkspaceVerifiedQueryListResponse {
  workspace_id: number;
  verified_queries: WorkspaceVerifiedQuery[];
}

export interface WorkspaceVerifiedQueryCreate {
  query_type: WorkspaceVerifiedQueryType;
  business_question: string;
  paraphrases?: string[];
  intent_tags?: string[];
  sql_dialect?: string | null;
  sql_text?: string | null;
  pseudocode?: string | null;
  expected_answer_contract?: Record<string, unknown> | null;
  structured_contract?: Record<string, unknown> | null;
  unstructured_contract?: Record<string, unknown> | null;
  table_refs?: string[];
  column_refs?: string[];
  metric_refs?: string[];
  entity_refs?: string[];
  relationship_refs?: string[];
  source_refs?: string[];
  citation_expectations?: Record<string, unknown> | null;
  validation_method?: string | null;
  status?: 'pending_review' | 'approved';
  source_surface?: string;
}

export interface WorkspaceVerifiedQueryUpdate extends Partial<Omit<WorkspaceVerifiedQueryCreate, 'status' | 'source_surface'>> {}

const semanticContextKey = (workspaceId: number) => ['workspace-semantic-context', workspaceId] as const;
const setupReadinessKey = (workspaceId: number) => ['workspace-semantic-context-setup-readiness', workspaceId] as const;
const mcpPolicyKey = (workspaceId: number) => ['workspace-semantic-context-mcp-policy', workspaceId] as const;
const mcpReadinessKey = (workspaceId: number) => ['workspace-semantic-context-mcp-readiness', workspaceId] as const;
const ontologyGenerationSettingsKey = (workspaceId: number) => ['workspace-ontology-generation-settings', workspaceId] as const;
const ontologyGenerationSuggestionsKey = (workspaceId: number) => ['workspace-ontology-generation-suggestions', workspaceId] as const;
const correctionProposalsKey = (workspaceId: number) => ['workspace-semantic-corrections', workspaceId] as const;
const verifiedQueriesKey = (workspaceId: number) => ['workspace-verified-queries', workspaceId] as const;
const warehouseProfilingLatestKey = (workspaceId: number, connectorId: number | null) =>
  ['workspace-warehouse-profiling-latest', workspaceId, connectorId] as const;
const warehouseProfilingEvidenceKey = (workspaceId: number, runId: number | null) =>
  ['workspace-warehouse-profiling-evidence', workspaceId, runId] as const;

function normalizeIdentifier(value: string): string {
  const normalized = value.trim().toLowerCase().replace(/[^a-z0-9_]+/g, '_').replace(/^_+|_+$/g, '');
  return normalized || 'semantic_item';
}

function parseNumericId(value: string): number | null {
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : null;
}

function normalizeStringList(values: string[] | undefined | null): string[] {
  const seen = new Set<string>();
  const normalized: string[] = [];
  (values ?? []).forEach((value) => {
    const trimmed = value.trim();
    const key = trimmed.toLowerCase();
    if (!trimmed || seen.has(key)) return;
    seen.add(key);
    normalized.push(trimmed);
  });
  return normalized;
}

function tableIdForEntity(entity: WorkspaceOntology['entities'][number], allowlist: StructuredAssetAllowlistRow[]): number | null {
  if (entity.physicalTable.startsWith('table:')) {
    return parseNumericId(entity.physicalTable.slice('table:'.length));
  }
  const table = allowlist.find((row) => `${row.dataset}.${row.objectName}` === entity.physicalTable);
  return table ? parseNumericId(table.id) : null;
}

function tableIdForPhysicalTable(ref: string, allowlist: StructuredAssetAllowlistRow[]): number | null {
  if (ref.startsWith('table:')) {
    return parseNumericId(ref.slice('table:'.length));
  }
  const table = allowlist.find((row) => `${row.dataset}.${row.objectName}` === ref);
  return table ? parseNumericId(table.id) : null;
}

export function mapWorkspaceOntologyToSemanticPayload(
  ontology: WorkspaceOntology,
  allowlist: StructuredAssetAllowlistRow[],
): SemanticContextOntologyResponse {
  const entityIdMap = new Map<string, number>();
  let nextTemporaryEntityId = -1;

  ontology.entities.forEach((entity) => {
    const numericId = parseNumericId(entity.id);
    if (numericId != null) {
      entityIdMap.set(entity.id, numericId);
      return;
    }
    entityIdMap.set(entity.id, nextTemporaryEntityId);
    nextTemporaryEntityId -= 1;
  });

  return {
    entities: ontology.entities.map((entity) => {
      const payloadId = entityIdMap.get(entity.id) ?? null;
      return {
        id: payloadId,
        name: normalizeIdentifier(entity.name),
        display_name: entity.name.trim() || 'Untitled entity',
        description: entity.description || null,
        aliases: normalizeStringList(entity.synonyms),
        ontology_ref: entity.ontologyRef ?? null,
        table_id: tableIdForEntity(entity, allowlist),
        source_table_ids: (entity.sourceBindings ?? []).map((binding) => Number(binding.tableId)),
        source_bindings: (entity.sourceBindings ?? []).map((binding) => ({
          table_id: Number(binding.tableId),
          binding_role: binding.bindingRole ?? null,
          source_authority: binding.sourceAuthority ?? null,
          identity_columns: binding.identityColumns,
          identity_mappings: (binding.identityMappings ?? []).map((mapping) => ({
            mapping_type: mapping.mappingType,
            local_column_ids: mapping.localColumnIds,
            local_column_names: mapping.localColumnNames,
            counterpart_table_id: mapping.counterpartTableId ?? null,
            counterpart_column_ids: mapping.counterpartColumnIds,
            counterpart_column_names: mapping.counterpartColumnNames,
            normalization_rules: mapping.normalizationRules,
            evidence_refs: mapping.evidenceRefs,
            confidence: mapping.confidence,
          })),
          normalization_rules: binding.normalizationRules,
          match_method: binding.matchMethod ?? null,
          match_evidence: binding.matchEvidence,
          grain: binding.grain ?? null,
          evidence: binding.evidence ?? null,
          confidence: binding.confidence,
          review_state: binding.reviewState,
          execution_mode: binding.executionMode,
          trust_contract: binding.trustContract
            ? {
                authority_tier: binding.trustContract.authorityTier,
                permitted_uses: binding.trustContract.permittedUses,
                conflict_policy: binding.trustContract.conflictPolicy,
                requires_steward_review: binding.trustContract.requiresStewardReview,
                notes: binding.trustContract.notes ?? null,
              }
            : null,
          conflict_state: binding.conflictState ?? 'none',
          conflicts: binding.conflicts,
        })),
        catalog_asset_id: entity.catalogAssetId ?? null,
      };
    }),
    semantic_mappings: (ontology.semanticMappings ?? []).flatMap((mapping) => {
      const tableId = tableIdForPhysicalTable(mapping.physicalTable, allowlist);
      const columnId = parseNumericId(mapping.columnId);
      if (tableId == null || columnId == null) {
        return [];
      }
      return [{
        id: parseNumericId(mapping.id),
        concept_key: mapping.conceptKey || normalizeIdentifier(mapping.label),
        label: mapping.label.trim() || 'Untitled concept',
        description: mapping.description || null,
        aliases: normalizeStringList(mapping.aliases),
        entity_id: entityIdMap.get(mapping.entityId) ?? null,
        table_id: tableId,
        column_id: columnId,
        catalog_asset_id: mapping.catalogAssetId ?? null,
        default_aggregation: mapping.defaultAggregation ?? null,
        value_kind: mapping.valueKind ?? null,
        semantic_metadata: mapping.semanticMetadata ?? null,
      }];
    }),
    metrics: ontology.metrics.map((metric) => ({
      id: parseNumericId(metric.id),
      name: normalizeIdentifier(metric.name),
      display_name: metric.name.trim() || 'Untitled metric',
      description: metric.description || null,
      entity_id: entityIdMap.get(metric.entityId) ?? null,
      sql_formula: metric.expression,
      expression_metadata: metric.expressionMetadata ?? null,
      primary_column_id: metric.primaryColumnId ? parseNumericId(metric.primaryColumnId) : null,
    })),
    relationships: ontology.joins
      .flatMap((join) => {
        const sourceEntityId = entityIdMap.get(join.leftEntityId);
        const targetEntityId = entityIdMap.get(join.rightEntityId);
        if (sourceEntityId == null || targetEntityId == null) {
          return [];
        }
        return [{
          id: parseNumericId(join.id),
          name: join.name || join.predicate || join.description || null,
          description: join.description || null,
          source_entity_id: sourceEntityId,
          target_entity_id: targetEntityId,
          relationship_type: 'many_to_one',
          relationship_kind: join.relationshipKind ?? (join.joinCondition.trim() ? 'join_backed' : 'semantic_only'),
          predicate: join.predicate || join.name || null,
          inverse_predicate: join.inversePredicate || null,
          source_role: join.sourceRole || null,
          target_role: join.targetRole || null,
          optionality: join.optionality ?? 'unknown',
          join_on: join.joinCondition.trim() || null,
          join_type: 'INNER',
          source_table_id: join.sourceTableId ? parseNumericId(join.sourceTableId) : null,
          target_table_id: join.targetTableId ? parseNumericId(join.targetTableId) : null,
          join_conditions: join.joinConditions ?? null,
          cardinality: join.cardinality ?? null,
          cardinality_warning: join.cardinalityWarning ?? null,
          status: join.status ?? 'approved',
        }];
      }),
  };
}

function mapReadiness(status: SemanticContextReadinessResponse['status']): TextToSqlReadinessState {
  if (status === 'ready') return 'approved';
  if (status === 'blocked') return 'blocked';
  if (status === 'configured') return 'configured';
  if (status === 'draft_pending') return 'draft_pending';
  return 'needs_setup';
}

const BLOCKER_ACTIONS: Record<string, { label: string; target: StructuredReadinessActionTarget }> = {
  warehouse_connector_missing: { label: 'Select connector', target: 'warehouse' },
  warehouse_connector_unhealthy: { label: 'Open connector', target: 'warehouse' },
  warehouse_scope_empty: { label: 'Select tables', target: 'assets' },
  ontology_empty: { label: 'Open ontology', target: 'ontology' },
  active_pack_missing: { label: 'Compile pack', target: 'runtime_pack' },
  catalog_enrichment_missing: { label: 'Review coverage', target: 'catalog' },
  catalog_enrichment_partial: { label: 'Review coverage', target: 'catalog' },
};

function withAction(blocker: StructuredReadinessBlocker): StructuredReadinessBlocker {
  const action = blocker.code ? BLOCKER_ACTIONS[blocker.code] : undefined;
  return action
    ? {
        ...blocker,
        actionLabel: blocker.actionLabel ?? action.label,
        actionTarget: blocker.actionTarget ?? action.target,
      }
    : blocker;
}

function hasBlocker(blockers: StructuredReadinessBlocker[], code: string): boolean {
  return blockers.some((blocker) => blocker.code === code);
}

function deriveCatalogEnrichmentBlocker(
  readiness: SemanticContextReadinessResponse,
): StructuredReadinessBlocker | null {
  if (readiness.selected_table_count <= 0) return null;
  if (readiness.catalog_link_count >= readiness.selected_table_count) return null;

  const missingCount = readiness.selected_table_count - readiness.catalog_link_count;
  const partial = readiness.catalog_link_count > 0;
  return {
    code: partial ? 'catalog_enrichment_partial' : 'catalog_enrichment_missing',
    severity: 'warning',
    message: partial
      ? `${missingCount} selected warehouse asset${missingCount === 1 ? '' : 's'} still lack metadata descriptions. Runtime activation can proceed, but catalog context will be thinner.`
      : 'Selected warehouse assets have no metadata descriptions yet. Runtime activation can proceed, but catalog context will be unavailable.',
    actionLabel: 'Review coverage',
    actionTarget: 'catalog',
  };
}

function formatRowCount(value?: number | null): string {
  return typeof value === 'number' ? `${value.toLocaleString()} rows` : '-';
}

function formatFreshness(value?: string | null): string {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : `Synced ${date.toLocaleDateString()}`;
}

function tableQualifiedName(table: SemanticContextTableSummary): string {
  return [table.project, table.dataset, table.name || table.table_external_id].filter(Boolean).join('.');
}

function mapAllowlistRows(tables: SemanticContextTableSummary[]): StructuredAssetAllowlistRow[] {
  return tables.map((table) => ({
    id: String(table.id),
    dataset: table.dataset || '-',
    objectName: table.name || table.table_external_id,
    objectType: 'table',
    rowEstimate: formatRowCount(table.row_count_snapshot),
    freshness: formatFreshness(table.last_synced_at),
    catalogBadge: table.catalog_link_count > 0 ? 'matched' : 'missing',
    certified: table.catalog_link_count > 0,
    selected: table.selected,
  }));
}

function mapCatalogRows(tables: SemanticContextTableSummary[]): WorkspaceCatalogCoverageRow[] {
  return tables.map((table) => ({
    id: String(table.id),
    qualifiedName: tableQualifiedName(table),
    resolverState: table.catalog_link_count > 0 ? 'resolved' : 'missing',
    ownerHint: null,
    sourceHint: table.catalog_link_count > 0 ? 'Warehouse, local, AI, or provider description' : null,
  }));
}

function mapOntology(ontology: SemanticContextOntologyResponse): WorkspaceOntology {
  return {
    entities: ontology.entities.map((entity) => {
      const bindings: NonNullable<
        SemanticContextOntologyResponse['entities'][number]['source_bindings']
      > = entity.source_bindings?.length
        ? entity.source_bindings
        : (entity.source_table_ids ?? []).map((tableId) => ({
          table_id: tableId,
          binding_role: tableId === entity.table_id ? 'primary' : null,
          source_authority: null,
          identity_columns: [],
          identity_mappings: [],
          normalization_rules: [],
          match_method: null,
          match_evidence: [],
          grain: null,
          evidence: null,
          confidence: 1,
          review_state: 'approved',
          execution_mode: 'source_local',
          trust_contract: null,
          conflict_state: 'none',
          conflicts: [],
        }));
      return {
        id: entity.id != null ? String(entity.id) : entity.name,
        name: entity.display_name || entity.name,
        physicalTable: entity.table_id != null ? `table:${entity.table_id}` : '-',
        sourceBindings: bindings.map((binding) => ({
          tableId: String(binding.table_id),
          bindingRole: binding.binding_role ?? null,
          sourceAuthority: binding.source_authority ?? null,
          identityColumns: binding.identity_columns ?? [],
          identityMappings: (binding.identity_mappings ?? []).map((mapping) => ({
            mappingType: mapping.mapping_type,
            localColumnIds: mapping.local_column_ids ?? [],
            localColumnNames: mapping.local_column_names ?? [],
            counterpartTableId: mapping.counterpart_table_id ?? null,
            counterpartColumnIds: mapping.counterpart_column_ids ?? [],
            counterpartColumnNames: mapping.counterpart_column_names ?? [],
            normalizationRules: mapping.normalization_rules ?? [],
            evidenceRefs: mapping.evidence_refs ?? [],
            confidence: mapping.confidence ?? 0,
          })),
          normalizationRules: binding.normalization_rules ?? [],
          matchMethod: binding.match_method ?? null,
          matchEvidence: binding.match_evidence ?? [],
          grain: binding.grain ?? null,
          evidence: binding.evidence ?? null,
          confidence: binding.confidence ?? 0,
          reviewState: binding.review_state ?? 'approved',
          executionMode: binding.execution_mode ?? 'source_local',
          trustContract: binding.trust_contract
            ? {
                authorityTier: binding.trust_contract.authority_tier ?? 'unknown',
                permittedUses: binding.trust_contract.permitted_uses ?? [],
                conflictPolicy: binding.trust_contract.conflict_policy ?? 'steward_required',
                requiresStewardReview: binding.trust_contract.requires_steward_review ?? true,
                notes: binding.trust_contract.notes ?? null,
              }
            : null,
          conflictState: binding.conflict_state ?? 'none',
          conflicts: binding.conflicts ?? [],
        })),
        description: entity.description || '',
        synonyms: normalizeStringList(entity.aliases),
        ontologyRef: entity.ontology_ref ?? null,
        catalogAssetId: entity.catalog_asset_id ?? null,
      };
    }),
    semanticMappings: (ontology.semantic_mappings ?? []).map((mapping) => ({
      id: mapping.id != null ? String(mapping.id) : mapping.concept_key,
      conceptKey: mapping.concept_key,
      label: mapping.label,
      entityId: mapping.entity_id != null ? String(mapping.entity_id) : '',
      physicalTable: `table:${mapping.table_id}`,
      columnId: String(mapping.column_id),
      description: mapping.description || '',
      aliases: normalizeStringList(mapping.aliases),
      defaultAggregation: mapping.default_aggregation ?? null,
      valueKind: mapping.value_kind ?? null,
      catalogAssetId: mapping.catalog_asset_id ?? null,
      semanticMetadata: mapping.semantic_metadata ?? null,
    })),
    metrics: ontology.metrics.map((metric) => ({
      id: metric.id != null ? String(metric.id) : metric.name,
      name: metric.display_name || metric.name,
      entityId: metric.entity_id != null ? String(metric.entity_id) : '',
      expression: metric.sql_formula,
      description: metric.description || '',
      primaryColumnId: metric.primary_column_id != null ? String(metric.primary_column_id) : null,
      expressionMetadata: metric.expression_metadata ?? null,
    })),
    joins: ontology.relationships.map((relationship) => ({
      id: relationship.id != null
        ? String(relationship.id)
        : String(relationship.join_on || relationship.name || `${relationship.source_entity_id}:${relationship.target_entity_id}`),
      name: relationship.name ?? null,
      leftEntityId: String(relationship.source_entity_id),
      rightEntityId: String(relationship.target_entity_id),
      joinCondition: relationship.join_on ?? '',
      relationshipKind: relationship.relationship_kind ?? (relationship.join_on ? 'join_backed' : 'semantic_only'),
      predicate: relationship.predicate ?? relationship.name ?? null,
      inversePredicate: relationship.inverse_predicate ?? null,
      sourceRole: relationship.source_role ?? null,
      targetRole: relationship.target_role ?? null,
      optionality: relationship.optionality ?? 'unknown',
      description: relationship.description || relationship.predicate || relationship.name || relationship.relationship_type,
      sourceTableId: relationship.source_table_id != null ? String(relationship.source_table_id) : null,
      targetTableId: relationship.target_table_id != null ? String(relationship.target_table_id) : null,
      joinConditions: relationship.join_conditions ?? null,
      joinEvidenceStatus: relationship.join_evidence_status ?? null,
      traversalAllowed: relationship.traversal_allowed ?? null,
      cardinality: relationship.cardinality ?? null,
      cardinalityWarning: relationship.cardinality_warning ?? null,
      status: relationship.status ?? 'approved',
    })),
  };
}

export function mapSemanticContextToStructuredDraft(
  context: SemanticContextOverviewResponse,
): StructuredAnalyticsDraft {
  const readiness = mapReadiness(context.readiness.status);
  const allowlistRows = mapAllowlistRows(context.structured_scope.scoped_tables);
  const catalogRows = mapCatalogRows(context.structured_scope.scoped_tables);
  const connector = context.structured_scope.warehouse_connector;
  const activePack = context.readiness.active_pack;
  const backendBlockers = context.readiness.blockers.map((blocker) =>
    withAction({
      code: blocker.code,
      severity: blocker.severity,
      message: blocker.message,
    }),
  );
  const catalogEnrichmentBlocker = deriveCatalogEnrichmentBlocker(context.readiness);
  const blockers =
    catalogEnrichmentBlocker && !hasBlocker(backendBlockers, catalogEnrichmentBlocker.code ?? '')
      ? [...backendBlockers, catalogEnrichmentBlocker]
      : backendBlockers;

  return {
    allowlist: allowlistRows,
    readiness,
    activeRuntimePackVersion: activePack ? `pack-v${activePack.version}` : null,
    ontologyVersionLabel: activePack ? `ontology/v${activePack.version}` : 'ontology/v0 (draft)',
    warehouseLabel: connector
      ? `${
          connector.connector_type === 'bigquery'
            ? 'BigQuery'
            : connector.connector_type === 'postgres'
              ? 'PostgreSQL'
              : 'Snowflake'
        } - ${connector.connector_name}`
      : null,
    warehouseStatus: connector ? (connector.is_healthy ? 'connected' : 'stale') : 'missing',
    catalogLabel: context.readiness.catalog_link_count > 0 ? 'Metadata coverage' : null,
    catalogIndexStatus:
      context.readiness.catalog_link_count > 0
        ? context.readiness.catalog_link_count >= context.readiness.selected_table_count
          ? 'healthy'
          : 'partial'
        : 'missing',
    catalogCoverageHint:
      context.readiness.catalog_link_count > 0
        ? `${context.readiness.catalog_link_count} selected warehouse assets have warehouse, local, AI, or provider descriptions.`
        : 'No metadata descriptions found for selected warehouse assets yet.',
    selectedAssetCount: context.readiness.selected_table_count,
    allowlistRows,
    catalogRows,
    ontology: mapOntology(context.ontology),
    blockers,
  };
}

export function useWorkspaceSemanticContext(workspaceId: number | null, enabled = true) {
  return useQuery({
    queryKey: workspaceId ? semanticContextKey(workspaceId) : ['workspace-semantic-context', 'missing'],
    queryFn: () =>
      customInstance<SemanticContextOverviewResponse>({
        url: `/api/v1/workspaces/${workspaceId}/semantic-context`,
        method: 'GET',
      }),
    enabled: Boolean(workspaceId) && enabled,
  });
}

export function useWorkspaceSemanticSetupReadiness(workspaceId: number | null, enabled = true) {
  return useQuery({
    queryKey: workspaceId ? setupReadinessKey(workspaceId) : ['workspace-semantic-context-setup-readiness', 'missing'],
    queryFn: () =>
      getSemanticContextSetupReadinessApiV1WorkspacesWorkspaceIdSemanticContextSetupReadinessGet(
        workspaceId!,
      ) as Promise<SemanticContextSetupReadinessResponse>,
    enabled: Boolean(workspaceId) && enabled,
  });
}

export function useRunWorkspaceSemanticReadinessSmoke(workspaceId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      runSemanticContextSetupReadinessSmokeApiV1WorkspacesWorkspaceIdSemanticContextSetupReadinessSmokePost(
        workspaceId!,
      ) as Promise<SemanticContextSetupReadinessResponse>,
    onSuccess: (data) => {
      if (workspaceId) {
        queryClient.setQueryData(setupReadinessKey(workspaceId), data);
      }
    },
  });
}

export function useLatestWorkspaceWarehouseProfilingRun(
  workspaceId: number | null,
  connectorId: number | null,
  enabled = true,
) {
  const queryClient = useQueryClient();
  const terminalRunIdRef = useRef<number | null>(null);
  const query = useQuery({
    queryKey:
      workspaceId && connectorId
        ? warehouseProfilingLatestKey(workspaceId, connectorId)
        : ['workspace-warehouse-profiling-latest', 'missing'],
    queryFn: () =>
      customInstance<WorkspaceWarehouseProfilingRun | null>({
        url: `/api/v1/workspaces/${workspaceId}/semantic-context/warehouse-profiling/runs/latest`,
        method: 'GET',
        params: { connector_id: connectorId },
      }),
    enabled: Boolean(workspaceId && connectorId) && enabled,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'pending' || status === 'running' ? 2_000 : false;
    },
  });
  useEffect(() => {
    const run = query.data;
    if (
      workspaceId &&
      run &&
      ['completed', 'completed_with_omissions', 'failed', 'cancelled'].includes(run.status) &&
      terminalRunIdRef.current !== run.id
    ) {
      terminalRunIdRef.current = run.id;
      void queryClient.invalidateQueries({ queryKey: setupReadinessKey(workspaceId) });
    }
  }, [query.data, queryClient, workspaceId]);
  return query;
}

export function useStartWorkspaceWarehouseProfilingRun(workspaceId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      connectorId,
      budget,
    }: {
      connectorId: number;
      budget?: WorkspaceWarehouseProfilingBudget;
    }) =>
      customInstance<WorkspaceWarehouseProfilingRun>({
        url: `/api/v1/workspaces/${workspaceId}/semantic-context/warehouse-profiling/runs`,
        method: 'POST',
        data: {
          connector_id: connectorId,
          ...(budget ? { budget } : {}),
        },
      }),
    onSuccess: (run) => {
      if (!workspaceId) return;
      queryClient.setQueryData(
        warehouseProfilingLatestKey(workspaceId, run.connector_id),
        run,
      );
      queryClient.invalidateQueries({ queryKey: setupReadinessKey(workspaceId) });
    },
  });
}

export function useWorkspaceWarehouseProfilingEvidence(
  workspaceId: number | null,
  runId: number | null,
  enabled = true,
) {
  return useQuery({
    queryKey:
      workspaceId && runId
        ? warehouseProfilingEvidenceKey(workspaceId, runId)
        : ['workspace-warehouse-profiling-evidence', 'missing'],
    queryFn: () =>
      customInstance<WorkspaceWarehouseProfilingEvidence>({
        url: `/api/v1/workspaces/${workspaceId}/semantic-context/warehouse-profiling/runs/${runId}/evidence`,
        method: 'GET',
      }),
    enabled: Boolean(workspaceId && runId) && enabled,
  });
}

export function useReviewWorkspaceWarehouseJoinEvidence(
  workspaceId: number | null,
  runId: number | null,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      evidenceId,
      action,
      reviewerNotes,
    }: {
      evidenceId: number;
      action: WorkspaceWarehouseJoinReviewAction;
      reviewerNotes?: string | null;
    }) =>
      customInstance<WorkspaceWarehouseJoinEvidence>({
        url: `/api/v1/workspaces/${workspaceId}/semantic-context/warehouse-profiling/join-evidence/${evidenceId}/review`,
        method: 'POST',
        data: {
          action,
          reviewer_notes: reviewerNotes?.trim() || null,
        },
      }),
    onSuccess: () => {
      if (!workspaceId) return;
      queryClient.invalidateQueries({
        queryKey: warehouseProfilingEvidenceKey(workspaceId, runId),
      });
      queryClient.invalidateQueries({ queryKey: setupReadinessKey(workspaceId) });
    },
  });
}

export function useWorkspaceSemanticMCPPolicy(workspaceId: number | null, enabled = true) {
  return useQuery({
    queryKey: workspaceId ? mcpPolicyKey(workspaceId) : ['workspace-semantic-context-mcp-policy', 'missing'],
    queryFn: () =>
      customInstance<SemanticContextMCPPolicyResponse>({
        url: `/api/v1/workspaces/${workspaceId}/semantic-context/mcp-policy`,
        method: 'GET',
      }),
    enabled: Boolean(workspaceId) && enabled,
  });
}

export function useWorkspaceSemanticMCPReadiness(workspaceId: number | null, enabled = true) {
  return useQuery({
    queryKey: workspaceId ? mcpReadinessKey(workspaceId) : ['workspace-semantic-context-mcp-readiness', 'missing'],
    queryFn: () =>
      customInstance<SemanticContextMCPReadinessResponse>({
        url: `/api/v1/workspaces/${workspaceId}/semantic-context/mcp-readiness`,
        method: 'GET',
      }),
    enabled: Boolean(workspaceId) && enabled,
  });
}

export function useUpdateWorkspaceSemanticStructuredScope(workspaceId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SemanticContextStructuredScopeUpdate) =>
      customInstance<SemanticContextStructuredScopeResponse>({
        url: `/api/v1/workspaces/${workspaceId}/semantic-context/structured-scope`,
        method: 'POST',
        data,
      }),
    onSuccess: () => {
      if (workspaceId) {
        queryClient.invalidateQueries({ queryKey: semanticContextKey(workspaceId) });
        queryClient.invalidateQueries({ queryKey: setupReadinessKey(workspaceId) });
        queryClient.invalidateQueries({ queryKey: ['workspace-table-scope', workspaceId] });
      }
    },
  });
}

export function useSaveWorkspaceSemanticOntologyDraft(workspaceId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SemanticContextOntologyResponse) =>
      customInstance<SemanticContextOntologyResponse>({
        url: `/api/v1/workspaces/${workspaceId}/semantic-context/ontology/draft`,
        method: 'PUT',
        data,
      }),
    onSuccess: (ontology) => {
      if (workspaceId) {
        queryClient.setQueryData<SemanticContextOverviewResponse>(
          semanticContextKey(workspaceId),
          (current) => current ? { ...current, ontology } : current,
        );
        queryClient.invalidateQueries({ queryKey: semanticContextKey(workspaceId) });
        queryClient.invalidateQueries({ queryKey: setupReadinessKey(workspaceId) });
      }
    },
  });
}

export function useCompileWorkspaceSemanticRuntimePack(workspaceId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      customInstance<SemanticContextCompileResponse>({
        url: `/api/v1/workspaces/${workspaceId}/semantic-context/runtime-packs/compile`,
        method: 'POST',
      }),
    onSuccess: () => {
      if (workspaceId) {
        queryClient.invalidateQueries({ queryKey: semanticContextKey(workspaceId) });
        queryClient.invalidateQueries({ queryKey: setupReadinessKey(workspaceId) });
        queryClient.invalidateQueries({ queryKey: mcpPolicyKey(workspaceId) });
        queryClient.invalidateQueries({ queryKey: mcpReadinessKey(workspaceId) });
        queryClient.invalidateQueries({ queryKey: ['workspace-semantic-runtime-packs', workspaceId] });
      }
    },
  });
}

export function useWorkspaceSemanticRuntimePacks(workspaceId: number | null) {
  return useQuery({
    queryKey: workspaceId ? ['workspace-semantic-runtime-packs', workspaceId] : ['workspace-semantic-runtime-packs', 'missing'],
    queryFn: () =>
      customInstance<SemanticContextRuntimePackListResponse>({
        url: `/api/v1/workspaces/${workspaceId}/semantic-context/runtime-packs`,
        method: 'GET',
      }),
    enabled: Boolean(workspaceId),
  });
}

export function useActivateWorkspaceSemanticRuntimePack(workspaceId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (packId: number) =>
      customInstance<SemanticContextCompileResponse>({
        url: `/api/v1/workspaces/${workspaceId}/semantic-context/runtime-packs/${packId}/activate`,
        method: 'POST',
      }),
    onSuccess: () => {
      if (workspaceId) {
        queryClient.invalidateQueries({ queryKey: semanticContextKey(workspaceId) });
        queryClient.invalidateQueries({ queryKey: setupReadinessKey(workspaceId) });
        queryClient.invalidateQueries({ queryKey: mcpPolicyKey(workspaceId) });
        queryClient.invalidateQueries({ queryKey: mcpReadinessKey(workspaceId) });
        queryClient.invalidateQueries({ queryKey: ['workspace-semantic-runtime-packs', workspaceId] });
      }
    },
  });
}

export function useWorkspaceOntologyGenerationSettings(workspaceId: number | null, enabled = true) {
  return useQuery({
    queryKey: workspaceId ? ontologyGenerationSettingsKey(workspaceId) : ['workspace-ontology-generation-settings', 'missing'],
    queryFn: () =>
      customInstance<OntologyGenerationSettingsResponse>({
        url: `/api/v1/workspaces/${workspaceId}/semantic-context/ontology-generation/settings`,
        method: 'GET',
      }),
    enabled: Boolean(workspaceId) && enabled,
  });
}

export function useWorkspaceSemanticCorrectionProposals(workspaceId: number | null) {
  return useQuery({
    queryKey: workspaceId ? correctionProposalsKey(workspaceId) : ['workspace-semantic-corrections', 'missing'],
    queryFn: () =>
      customInstance<WorkspaceSemanticCorrectionListResponse>({
        url: `/api/v1/workspaces/${workspaceId}/semantic-context/correction-proposals`,
        method: 'GET',
      }),
    enabled: Boolean(workspaceId),
  });
}

export function useWorkspaceVerifiedQueries(workspaceId: number | null, enabled = true) {
  return useQuery({
    queryKey: workspaceId ? verifiedQueriesKey(workspaceId) : ['workspace-verified-queries', 'missing'],
    queryFn: () =>
      customInstance<WorkspaceVerifiedQueryListResponse>({
        url: `/api/v1/workspaces/${workspaceId}/semantic-context/verified-queries`,
        method: 'GET',
      }),
    enabled: Boolean(workspaceId) && enabled,
  });
}

export function useWorkspaceOntologyGenerationSuggestions(workspaceId: number | null, enabled = true) {
  return useQuery({
    queryKey: workspaceId ? ontologyGenerationSuggestionsKey(workspaceId) : ['workspace-ontology-generation-suggestions', 'missing'],
    queryFn: async () =>
      normalizeOntologySuggestionsPayload(
        await customInstance<unknown>({
          url: `/api/v1/workspaces/${workspaceId}/semantic-context/ontology-generation/suggestions`,
          method: 'GET',
          params: { suggestion_status: 'pending' },
        }),
      ),
    enabled: Boolean(workspaceId) && enabled,
  });
}

export function normalizeOntologySuggestionsPayload(
  payload: unknown,
): OntologySuggestionResponse[] {
  if (!Array.isArray(payload)) {
    throw new Error('Ontology suggestions response must be an array');
  }
  return payload as OntologySuggestionResponse[];
}

export function useCreateWorkspaceOntologyGenerationRun(workspaceId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      customInstance<OntologyGenerationRunResponse>({
        url: `/api/v1/workspaces/${workspaceId}/semantic-context/ontology-generation/runs`,
        method: 'POST',
      }),
    onSuccess: () => {
      if (workspaceId) {
        queryClient.invalidateQueries({ queryKey: ontologyGenerationSuggestionsKey(workspaceId) });
        queryClient.invalidateQueries({ queryKey: semanticContextKey(workspaceId) });
        queryClient.invalidateQueries({ queryKey: setupReadinessKey(workspaceId) });
      }
    },
  });
}

export function useWorkspaceOntologyGenerationRunStream(
  workspaceId: number | null,
  requestedRunId: number | null,
  isPersistedReplay = false,
): OntologyGenerationStreamState {
  const queryClient = useQueryClient();
  const [events, setEvents] = useState<OntologyGenerationRunEvent[]>([]);
  const [resolvedRunId, setResolvedRunId] = useState<number | undefined>(undefined);
  const [connectionState, setConnectionState] =
    useState<OntologyGenerationConnectionState>('idle');
  const [wasRestored, setWasRestored] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const eventSourceRef = useRef<EventSource | null>(null);
  const lastEventIdRef = useRef(0);

  useEffect(() => {
    if (!workspaceId) {
      setEvents([]);
      setResolvedRunId(undefined);
      setConnectionState('idle');
      setWasRestored(false);
      setErrorMessage(undefined);
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      return;
    }

    setEvents([]);
    setResolvedRunId(undefined);
    setWasRestored(false);
    setErrorMessage(undefined);

    const token = localStorage.getItem('auth_token');
    if (!token) {
      setConnectionState('permission_denied');
      setErrorMessage('Sign in again to view ontology generation progress.');
      return;
    }

    lastEventIdRef.current = 0;
    let disposed = false;
    let terminal = false;
    let activeRunId: number | null = requestedRunId;
    let pollingTimer: number | null = null;
    let reconnectTimer: number | null = null;
    let transportMode: OntologyGenerationConnectionState = 'connecting';

    const appendGenerationEvent = (generationEvent: OntologyGenerationRunEvent) => {
      const rawEventId = generationEvent.id ?? generationEvent.event_id;
      const parsedEventId =
        typeof rawEventId === 'number' ? rawEventId : Number.parseInt(String(rawEventId), 10);
      const hasStableEventId = Number.isFinite(parsedEventId);
      const normalizedEvent = {
        ...generationEvent,
        id: hasStableEventId ? parsedEventId : Date.now() * -1,
      };
      if (hasStableEventId) {
        lastEventIdRef.current = Math.max(lastEventIdRef.current, parsedEventId);
      }
      setEvents((prev) => {
        if (hasStableEventId && prev.some((existing) => existing.id === parsedEventId)) {
          return prev;
        }
        return [...prev, normalizedEvent].sort((left, right) => left.id - right.id);
      });
    };

    const clearTimers = () => {
      if (pollingTimer) {
        window.clearInterval(pollingTimer);
        pollingTimer = null;
      }
      if (reconnectTimer) {
        window.clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };

    const closeStream = () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };

    const handleTerminalEvent = (generationEvent: OntologyGenerationRunEvent) => {
      if (generationEvent.event_type === 'run_completed') {
        terminal = true;
        clearTimers();
        closeStream();
        setConnectionState('completed');
        queryClient.invalidateQueries({ queryKey: ontologyGenerationSuggestionsKey(workspaceId) });
        queryClient.invalidateQueries({ queryKey: semanticContextKey(workspaceId) });
        queryClient.invalidateQueries({ queryKey: setupReadinessKey(workspaceId) });
      }
      if (
        generationEvent.event_type === 'run_failed' ||
        generationEvent.event_type === 'run_superseded'
      ) {
        terminal = true;
        clearTimers();
        closeStream();
        setConnectionState('failed');
      }
    };

    const resolveActiveRun = async (): Promise<number | null> => {
      if (requestedRunId) return requestedRunId;
      const run = await customInstance<OntologyGenerationRunResponse | null>({
        url: `/api/v1/workspaces/${workspaceId}/semantic-context/ontology-generation/runs/latest-resumable`,
        method: 'GET',
      });
      return run?.id ?? null;
    };

    const pollPersistedEvents = async () => {
      if (disposed || terminal || !activeRunId) return;
      try {
        const generationEvents = await customInstance<OntologyGenerationRunEvent[]>({
          url: `/api/v1/workspaces/${workspaceId}/semantic-context/ontology-generation/runs/${activeRunId}/events`,
          method: 'GET',
          params: { after_id: lastEventIdRef.current },
        });
        generationEvents.forEach((generationEvent) => {
          appendGenerationEvent(generationEvent);
          handleTerminalEvent(generationEvent);
        });
        setErrorMessage(undefined);
      } catch (pollingError: unknown) {
        const responseStatus = (
          pollingError as { response?: { status?: number } }
        )?.response?.status;
        if (responseStatus === 401 || responseStatus === 403) {
          terminal = true;
          clearTimers();
          closeStream();
          setConnectionState('permission_denied');
          setErrorMessage('You do not have permission to view this generation run.');
          return;
        }
        setErrorMessage('Live updates are unavailable. Retrying with persisted events.');
      }
    };

    const startPolling = () => {
      if (disposed || terminal || pollingTimer) return;
      transportMode = 'polling';
      setConnectionState('polling');
      void pollPersistedEvents();
      pollingTimer = window.setInterval(() => {
        void pollPersistedEvents();
      }, 2500);
    };

    const connectStream = () => {
      if (disposed || terminal || !activeRunId) return;
      closeStream();
      const streamUrl = browserApiUrl(
        `/api/v1/workspaces/${workspaceId}/semantic-context/ontology-generation/runs/${activeRunId}/stream`,
      );
      const eventSource = new EventSource(
        `${streamUrl}?token=${encodeURIComponent(token)}&after_id=${lastEventIdRef.current}`,
        { withCredentials: false },
      );
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        if (disposed || terminal) return;
        const recovered = transportMode === 'polling';
        transportMode = recovered ? 'recovered' : 'live';
        if (pollingTimer) {
          window.clearInterval(pollingTimer);
          pollingTimer = null;
        }
        setConnectionState(recovered ? 'recovered' : 'live');
        setErrorMessage(undefined);
      };
      eventSource.onmessage = (event) => {
        try {
          const generationEvent: OntologyGenerationRunEvent = JSON.parse(event.data);
          appendGenerationEvent(generationEvent);
          handleTerminalEvent(generationEvent);
        } catch {
          setErrorMessage('A persisted generation event could not be read.');
        }
      };
      eventSource.onerror = () => {
        if (disposed || terminal) return;
        eventSource.close();
        if (eventSourceRef.current === eventSource) eventSourceRef.current = null;
        startPolling();
        if (!reconnectTimer) {
          reconnectTimer = window.setTimeout(() => {
            reconnectTimer = null;
            connectStream();
          }, 10000);
        }
      };
    };

    const begin = async () => {
      setConnectionState('connecting');
      setErrorMessage(undefined);
      try {
        activeRunId = await resolveActiveRun();
      } catch (runError: unknown) {
        const responseStatus = (
          runError as { response?: { status?: number } }
        )?.response?.status;
        setConnectionState(
          responseStatus === 401 || responseStatus === 403 ? 'permission_denied' : 'idle',
        );
        setErrorMessage(
          responseStatus === 401 || responseStatus === 403
            ? 'You do not have permission to view ontology generation runs.'
            : 'Generation status could not be loaded.',
        );
        return;
      }
      if (disposed || !activeRunId) {
        setResolvedRunId(undefined);
        setWasRestored(false);
        setConnectionState('idle');
        return;
      }
      setResolvedRunId(activeRunId);
      setWasRestored(!requestedRunId);
      await pollPersistedEvents();
      if (!terminal && !disposed) connectStream();
    };

    void begin();

    return () => {
      disposed = true;
      clearTimers();
      closeStream();
    };
  }, [queryClient, requestedRunId, workspaceId]);

  const latestEvent = [...events].reverse().find((event) => event.event_type !== 'run_started') ?? events[events.length - 1];
  const completion = [...events]
    .reverse()
    .find((event) => event.event_type === 'run_completed');
  const failure = [...events]
    .reverse()
    .find(
      (event) =>
        event.event_type === 'run_failed' ||
        event.event_type === 'run_superseded',
    );
  const progressPercentage =
    typeof latestEvent?.progress_percentage === 'number'
      ? Math.min(100, Math.max(0, latestEvent.progress_percentage))
      : undefined;

  return {
    events,
    latestEvent,
    completion,
    failure,
    runId: resolvedRunId,
    isConnected: connectionState === 'live' || connectionState === 'recovered',
    isStreaming: Boolean(
      resolvedRunId &&
        !completion &&
        !failure &&
        !['idle', 'permission_denied', 'completed', 'failed'].includes(connectionState),
    ),
    connectionState,
    isPolling: connectionState === 'polling',
    wasRestored,
    isPersistedReplay,
    progressMessage: latestEvent?.message ?? undefined,
    progressPercentage,
    errorMessage,
  };
}

export function useCreateWorkspaceVerifiedQuery(workspaceId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: WorkspaceVerifiedQueryCreate) =>
      customInstance<WorkspaceVerifiedQuery>({
        url: `/api/v1/workspaces/${workspaceId}/semantic-context/verified-queries`,
        method: 'POST',
        data,
      }),
    onSuccess: () => {
      if (workspaceId) {
        queryClient.invalidateQueries({ queryKey: verifiedQueriesKey(workspaceId) });
      }
    },
  });
}

export function useDraftWorkspaceVerifiedQueryWithAI(workspaceId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { query_type: WorkspaceVerifiedQueryType; business_question: string }) =>
      customInstance<WorkspaceVerifiedQuery>({
        url: `/api/v1/workspaces/${workspaceId}/semantic-context/verified-queries/draft-with-ai`,
        method: 'POST',
        data,
      }),
    onSuccess: () => {
      if (workspaceId) {
        queryClient.invalidateQueries({ queryKey: verifiedQueriesKey(workspaceId) });
      }
    },
  });
}

export function useUpdateWorkspaceVerifiedQuery(workspaceId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ queryId, data }: { queryId: number; data: WorkspaceVerifiedQueryUpdate }) =>
      customInstance<WorkspaceVerifiedQuery>({
        url: `/api/v1/workspaces/${workspaceId}/semantic-context/verified-queries/${queryId}`,
        method: 'PUT',
        data,
      }),
    onSuccess: () => {
      if (workspaceId) {
        queryClient.invalidateQueries({ queryKey: verifiedQueriesKey(workspaceId) });
        queryClient.invalidateQueries({ queryKey: semanticContextKey(workspaceId) });
      }
    },
  });
}

export function useReviewWorkspaceVerifiedQuery(workspaceId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      queryId,
      action,
      reviewer_notes,
    }: {
      queryId: number;
      action: 'approve' | 'reject';
      reviewer_notes?: string | null;
    }) =>
      customInstance<WorkspaceVerifiedQuery>({
        url: `/api/v1/workspaces/${workspaceId}/semantic-context/verified-queries/${queryId}/${action}`,
        method: 'POST',
        data: { reviewer_notes },
      }),
    onSuccess: () => {
      if (workspaceId) {
        queryClient.invalidateQueries({ queryKey: verifiedQueriesKey(workspaceId) });
        queryClient.invalidateQueries({ queryKey: semanticContextKey(workspaceId) });
      }
    },
  });
}

export function useDeleteWorkspaceVerifiedQuery(workspaceId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (queryId: number) =>
      customInstance<void>({
        url: `/api/v1/workspaces/${workspaceId}/semantic-context/verified-queries/${queryId}`,
        method: 'DELETE',
      }),
    onSuccess: () => {
      if (workspaceId) {
        queryClient.invalidateQueries({ queryKey: verifiedQueriesKey(workspaceId) });
        queryClient.invalidateQueries({ queryKey: semanticContextKey(workspaceId) });
      }
    },
  });
}

export function useCreateWorkspaceSemanticCorrectionProposal(workspaceId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: WorkspaceSemanticCorrectionCreate) =>
      customInstance<WorkspaceSemanticCorrectionProposal>({
        url: `/api/v1/workspaces/${workspaceId}/semantic-context/correction-proposals`,
        method: 'POST',
        data,
      }),
    onSuccess: () => {
      if (workspaceId) {
        queryClient.invalidateQueries({ queryKey: correctionProposalsKey(workspaceId) });
        queryClient.invalidateQueries({ queryKey: semanticContextKey(workspaceId) });
      }
    },
  });
}

export function useApplyWorkspaceOntologySuggestion(workspaceId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (suggestionId: number) =>
      customInstance<OntologySuggestionResponse>({
        url: `/api/v1/workspaces/${workspaceId}/semantic-context/ontology-generation/suggestions/${suggestionId}/apply`,
        method: 'POST',
        data: {},
      }),
    onSuccess: () => {
      if (workspaceId) {
        queryClient.invalidateQueries({ queryKey: ontologyGenerationSuggestionsKey(workspaceId) });
        queryClient.invalidateQueries({ queryKey: semanticContextKey(workspaceId) });
        queryClient.invalidateQueries({ queryKey: setupReadinessKey(workspaceId) });
      }
    },
  });
}

export function useApplyWorkspaceOntologySuggestionsBatch(workspaceId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      runId,
      suggestionIds,
    }: {
      runId: number;
      suggestionIds: number[];
    }) => {
      if (!workspaceId) {
        throw new Error('Workspace is required to apply ontology suggestions');
      }
      return applyOntologyGenerationSuggestionsBatchApiV1WorkspacesWorkspaceIdSemanticContextOntologyGenerationRunsRunIdSuggestionsApplyPost(
        workspaceId,
        runId,
        {
          suggestion_ids: suggestionIds,
        },
      );
    },
    onSuccess: () => {
      if (workspaceId) {
        queryClient.invalidateQueries({ queryKey: ontologyGenerationSuggestionsKey(workspaceId) });
        queryClient.invalidateQueries({ queryKey: semanticContextKey(workspaceId) });
        queryClient.invalidateQueries({ queryKey: setupReadinessKey(workspaceId) });
      }
    },
  });
}

export function useReviewWorkspaceSemanticCorrectionProposal(workspaceId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      proposalId,
      action,
      reviewer_notes,
      target_ref,
      field_path,
      proposed_value,
      proposed_patch,
    }: {
      proposalId: number;
      action: 'approve' | 'reject';
      reviewer_notes?: string | null;
      target_ref?: Record<string, unknown> | null;
      field_path?: string | null;
      proposed_value?: string | null;
      proposed_patch?: Record<string, unknown> | null;
    }) =>
      customInstance<WorkspaceSemanticCorrectionProposal>({
        url: `/api/v1/workspaces/${workspaceId}/semantic-context/correction-proposals/${proposalId}/${action}`,
        method: 'POST',
        data: { reviewer_notes, target_ref, field_path, proposed_value, proposed_patch },
      }),
    onSuccess: () => {
      if (workspaceId) {
        queryClient.invalidateQueries({ queryKey: correctionProposalsKey(workspaceId) });
        queryClient.invalidateQueries({ queryKey: semanticContextKey(workspaceId) });
        queryClient.invalidateQueries({ queryKey: setupReadinessKey(workspaceId) });
      }
    },
  });
}

export function useDeleteWorkspaceSemanticCorrectionProposal(workspaceId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (proposalId: number) =>
      customInstance<void>({
        url: `/api/v1/workspaces/${workspaceId}/semantic-context/correction-proposals/${proposalId}`,
        method: 'DELETE',
      }),
    onSuccess: () => {
      if (workspaceId) {
        queryClient.invalidateQueries({ queryKey: correctionProposalsKey(workspaceId) });
        queryClient.invalidateQueries({ queryKey: semanticContextKey(workspaceId) });
      }
    },
  });
}

export function useRejectWorkspaceOntologySuggestion(workspaceId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (suggestionId: number) =>
      customInstance<OntologySuggestionResponse>({
        url: `/api/v1/workspaces/${workspaceId}/semantic-context/ontology-generation/suggestions/${suggestionId}/reject`,
        method: 'POST',
        data: {},
      }),
    onSuccess: () => {
      if (workspaceId) {
        queryClient.invalidateQueries({ queryKey: ontologyGenerationSuggestionsKey(workspaceId) });
        queryClient.invalidateQueries({ queryKey: semanticContextKey(workspaceId) });
        queryClient.invalidateQueries({ queryKey: setupReadinessKey(workspaceId) });
      }
    },
  });
}

export function useRouteWorkspaceSemanticCorrectionProposal(workspaceId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      proposalId,
      route,
    }: {
      proposalId: number;
      route: WorkspaceSemanticCorrectionRoute;
    }) =>
      customInstance<WorkspaceSemanticCorrectionProposal>({
        url: `/api/v1/workspaces/${workspaceId}/semantic-context/correction-proposals/${proposalId}/route`,
        method: 'POST',
        data: route,
      }),
    onSuccess: () => {
      if (workspaceId) {
        queryClient.invalidateQueries({ queryKey: correctionProposalsKey(workspaceId) });
      }
    },
  });
}
